// Every model invocation across every tenant, newest first, with the
// per-touchpoint aggregates above the feed.
import { useState } from "react";
import { ScrollText } from "lucide-react";
import { useNavigate } from "react-router";
import type { LlmRunStatus, LlmTouchpointSlug } from "@gb/admin-contracts";
import { LlmRunStatusSchema, LlmTouchpointSlugSchema } from "@gb/admin-contracts";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRuns, useRunStats } from "./runs-api";
import { useTenants } from "../tenants/tenants-api";

const STATUS_TONES: Record<string, string> = {
  ok: "text-ok",
  skipped: "text-faint",
};

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  placeholder: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-white px-2 font-mono text-[0.75rem] text-ink"
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

export function RunsPage() {
  const [tenant, setTenant] = useState("");
  const [touchpoint, setTouchpoint] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();

  const tenants = useTenants();
  const stats = useRunStats();
  const runs = useRuns({
    offset,
    ...(tenant ? { tenant } : {}),
    ...(touchpoint ? { touchpoint: touchpoint as LlmTouchpointSlug } : {}),
    ...(status ? { status: status as LlmRunStatus } : {}),
  });

  return (
    <div>
      <PageHeader eyebrow="Platform" title="Model runs" />

      {stats.data && stats.data.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {stats.data.map((s) => (
            <div key={s.touchpoint} className="rounded-md border border-border-inner bg-white px-3 py-2">
              <div className="font-mono text-[0.7rem] text-faint">{s.touchpoint}</div>
              <div className="font-mono text-[0.78rem] text-ink">
                {s.count} runs · {s.errorCount} not ok
                {s.p50LatencyMs !== null ? ` · p50 ${Math.round(s.p50LatencyMs)}ms` : ""} ·{" "}
                {s.totalInputTokens + s.totalOutputTokens} tok
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Select
          value={tenant}
          onChange={(v) => {
            setTenant(v);
            setOffset(0);
          }}
          options={(tenants.data ?? []).map((t) => t.slug)}
          placeholder="all tenants"
        />
        <Select
          value={touchpoint}
          onChange={(v) => {
            setTouchpoint(v);
            setOffset(0);
          }}
          options={LlmTouchpointSlugSchema.options}
          placeholder="all touchpoints"
        />
        <Select
          value={status}
          onChange={(v) => {
            setStatus(v);
            setOffset(0);
          }}
          options={LlmRunStatusSchema.options}
          placeholder="all statuses"
        />
      </div>

      <div className="mt-4">
        {runs.isError ? (
          <EmptyState icon={ScrollText} title="Couldn’t load runs" description={String(runs.error)} />
        ) : (
          <>
            <DataTable
              columns={[
                {
                  key: "at",
                  header: "When",
                  width: 150,
                  render: (r) => (
                    <span className="font-mono text-[0.72rem] text-faint">
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                  ),
                },
                {
                  key: "tenant",
                  header: "Tenant",
                  width: 100,
                  render: (r) => (
                    <span className="font-mono text-[0.72rem] text-muted">{r.tenantSlug}</span>
                  ),
                },
                {
                  key: "touchpoint",
                  header: "Touchpoint",
                  render: (r) => (
                    <div className="min-w-0">
                      <div className="truncate font-mono text-[0.78rem] text-ink">
                        {r.touchpoint}
                      </div>
                      <div className="truncate font-mono text-[0.68rem] text-faint">
                        {r.provider} · {r.model} · v{r.promptVersion}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  width: 110,
                  render: (r) => (
                    <span
                      className={cn(
                        "font-mono text-[0.75rem]",
                        STATUS_TONES[r.status] ?? "text-danger",
                      )}
                    >
                      {r.status}
                    </span>
                  ),
                },
                {
                  key: "cost",
                  header: "Tokens / latency",
                  width: 170,
                  render: (r) => (
                    <span className="font-mono text-[0.72rem] text-muted">
                      {r.inputTokens ?? "?"}→{r.outputTokens ?? "?"} · {r.latencyMs}ms
                    </span>
                  ),
                },
              ]}
              rows={runs.data?.runs ?? []}
              getRowKey={(r) => r.id}
              onSelect={(r) => void navigate(`/runs/${r.tenantSlug}/${r.id}`)}
              emptyMessage="No runs recorded yet."
            />
            {runs.data && runs.data.total > 50 && (
              <div className="mt-3 flex items-center gap-3">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - 50))}
                >
                  Newer
                </Button>
                <span className="font-mono text-[0.72rem] text-faint">
                  {offset + 1}–{Math.min(offset + 50, runs.data.total)} of {runs.data.total}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={offset + 50 >= runs.data.total}
                  onClick={() => setOffset(offset + 50)}
                >
                  Older
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
